// Request de-duplication + a short result cache, for GETs the borrower page
// makes on mount.
//
// WHY THIS EXISTS
// The Compare-offers and Talk-to-an-RM panels each fetch their catalogue in an
// effect with `[]` deps — "once per mount". That is correct as written, but it
// makes the request count a function of how many times the component mounts,
// and on production /borrower each endpoint was observed firing THREE times in
// a single page load. React's StrictMode double-invokes effects in development
// too, so the same call goes out twice every time anyone runs the dev server.
//
// Rather than hunt every possible remount, this makes a duplicate call cheap:
//
//   · IN FLIGHT — a second caller for the same key gets the FIRST call's
//     promise instead of opening a second request. This is what collapses
//     StrictMode's double-invoke, and any two components asking at once.
//   · RECENTLY RESOLVED — a caller within `ttlMs` gets the stored value with no
//     network at all. This is what collapses a remount a moment later.
//
// Errors are deliberately NOT cached: a failed catalogue call must be
// retryable on the next mount, and the panels render a real "could not load"
// state off that rejection.
//
// This is the same reasoning the report-services hook applies (see CLAUDE.md,
// "Credits Required System"): dedupe and cache with a short TTL, rather than
// refetch-on-every-mount. That one uses TanStack Query; this app does not carry
// it as a dependency, and two endpoints do not earn one.

const inflight = new Map();
const cache = new Map();

/** Sixty seconds: long enough to cover a remount storm, short enough that a
 *  reader who reloads to see fresh rates gets them. */
const DEFAULT_TTL_MS = 60000;

/**
 * @param {string}   key    identity of the request — endpoint + its params.
 * @param {Function} run    () => Promise, called only on a real miss.
 * @param {number}   ttlMs  how long a resolved value stays servable.
 */
export function dedupe(key, run, ttlMs = DEFAULT_TTL_MS) {
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < ttlMs) return Promise.resolve(hit.value);

  const flying = inflight.get(key);
  if (flying) return flying;

  const p = run()
    .then((value) => {
      cache.set(key, { at: Date.now(), value });
      return value;
    })
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, p);
  return p;
}

/** Drop everything — for a sign-in/sign-out, where the tenant context changes
 *  and yesterday's catalogue is the wrong one. */
export function clearDedupeCache() {
  inflight.clear();
  cache.clear();
}

export default dedupe;
